import React, { useState } from 'react';
import { useFillHeight } from '../hooks/useViewportFit';
import { Typography, Card, Steps, Collapse, Tag, Divider, Row, Col, Space, Button } from 'antd';
import {
  LoginOutlined, DashboardOutlined, ScheduleOutlined, BellOutlined,
  SearchOutlined, ExperimentOutlined, QuestionCircleOutlined,
  CheckCircleOutlined, EyeOutlined, FileTextOutlined,
  SafetyOutlined, PhoneOutlined, MailOutlined, ClockCircleOutlined,
  LeftOutlined, RightOutlined,
} from '@ant-design/icons';

const { Title, Text, Paragraph } = Typography;

const steps = [
  {
    title: '로그인',
    icon: <LoginOutlined />,
    description: '의뢰사 계정으로 접속',
    detail: '관리자로부터 발급받은 아이디와 비밀번호로 로그인합니다. 로그인 후 자사 의뢰 시험 정보만 조회할 수 있습니다.',
  },
  {
    title: '대시보드 확인',
    icon: <DashboardOutlined />,
    description: '시험 현황 요약',
    detail: '전체 시험 건수, 동/하절기별 진행 현황을 한눈에 확인합니다. 상태별 분포 차트와 최근 활동 목록도 제공됩니다.',
  },
  {
    title: '시험 일정 조회',
    icon: <ScheduleOutlined />,
    description: '시험 목록 검색',
    detail: '의뢰하신 시험의 전체 목록을 조회합니다. 상태별 필터와 키워드 검색으로 원하는 시험을 빠르게 찾을 수 있습니다.',
  },
  {
    title: '상세 정보 확인 및 파트너확인',
    icon: <EyeOutlined />,
    description: '시험 상세 + 타임라인 + 파트너확인',
    detail: '시험 목록에서 행을 클릭하면 상세 팝업이 열립니다. 시험번호, 구분, 장소 등 상세 정보와 처리/조사 타임라인을 확인하고, "파트너확인" 버튼으로 수신 확인을 기록할 수 있습니다.',
  },
  {
    title: '알림 확인',
    icon: <BellOutlined />,
    description: '상태 변경 알림',
    detail: '시험 상태가 변경되면 알림이 발송됩니다. 알림 페이지에서 읽지 않은 알림을 확인하세요.',
  },
];

const faqItems = [
  {
    key: '1',
    label: '계정을 어떻게 발급받나요?',
    children: '시험 의뢰 시 담당 연구원에게 파트너 포털 계정 발급을 요청하세요. 의뢰사별로 1개 이상의 계정을 발급받을 수 있습니다.',
  },
  {
    key: '2',
    label: '비밀번호를 분실했어요.',
    children: '시험기관 관리자에게 비밀번호 초기화를 요청하세요. 보안을 위해 초기화된 비밀번호는 반드시 로그인 후 변경해주세요.',
  },
  {
    key: '3',
    label: '다른 회사의 시험 정보도 볼 수 있나요?',
    children: '아니요. 파트너 포털은 자사가 의뢰한 시험 정보만 조회할 수 있습니다. 보안을 위해 다른 의뢰사의 데이터에는 접근할 수 없습니다.',
  },
  {
    key: '4',
    label: '시험 진행 상태는 어떤 종류가 있나요?',
    children: '처리완료 → 조사완료 → 성적서작성중 → 성적서완료 → 발급완료 → 최종종료의 6단계 상태가 있습니다. 각 시험의 처리 차수와 조사 차수별로 세부 진행 상황도 타임라인으로 확인할 수 있습니다.',
  },
  {
    key: '5',
    label: '성적서/보고서는 어디서 다운로드하나요?',
    children: '시험 상세 페이지에서 첨부된 문서를 다운로드할 수 있습니다. 문서가 업로드되면 알림으로 안내됩니다.',
  },
  {
    key: '6',
    label: '시험 데이터는 얼마나 자주 업데이트되나요?',
    children: '연구원이 시험 데이터를 등록/수정하면 실시간으로 반영됩니다. 포털에서 항상 최신 정보를 확인할 수 있습니다.',
  },
];

const UserGuidePage: React.FC = () => {
  const [currentStep, setCurrentStep] = useState(0);
  /* 긴 가이드 문서는 뷰포트 바닥까지 실측한 컨테이너 안에서만 스크롤 — 문서 세로스크롤 방지 */
  const { ref: fillRef, height: fillHeight } = useFillHeight(360);

  return (
    <div ref={fillRef} style={{ height: fillHeight, overflowY: 'auto' }}>
    <div style={{ maxWidth: 960, margin: '0 auto' }}>
      <div style={{ marginBottom: 32 }}>
        <Title level={4} style={{ margin: 0, color: '#92400e' }}>
          <QuestionCircleOutlined style={{ marginRight: 8 }} />
          사용자 가이드
        </Title>
        <Text type="secondary">SAPPT 파트너 포털 이용 안내</Text>
      </div>

      {/* 이용 흐름 */}
      <Card
        title={<Text strong style={{ color: '#92400e', fontSize: 16 }}>이용 흐름</Text>}
        style={{ marginBottom: 24, borderColor: '#fde68a' }}
      >
        <Steps
          current={currentStep}
          onChange={setCurrentStep}
          items={steps.map(s => ({
            title: s.title,
            description: s.description,
            icon: s.icon,
          }))}
          style={{ marginBottom: 24 }}
        />
        <Card
          size="small"
          style={{ background: '#fffbeb', borderColor: '#fde68a' }}
        >
          <Space direction="vertical" size={8}>
            <Text strong style={{ color: '#92400e', fontSize: 15 }}>
              {steps[currentStep].icon} {steps[currentStep].title}
            </Text>
            <Paragraph style={{ margin: 0, color: '#78350f', lineHeight: 1.8 }}>
              {steps[currentStep].detail}
            </Paragraph>
          </Space>
          <div style={{ marginTop: 16, display: 'flex', justifyContent: 'space-between' }}>
            <Button
              size="small"
              icon={<LeftOutlined />}
              disabled={currentStep === 0}
              onClick={() => setCurrentStep(currentStep - 1)}
            >
              이전
            </Button>
            <Button
              size="small"
              type="primary"
              disabled={currentStep === steps.length - 1}
              onClick={() => setCurrentStep(currentStep + 1)}
            >
              다음 <RightOutlined />
            </Button>
          </div>
        </Card>
      </Card>

      {/* 주요 기능 소개 */}
      <Card
        title={<Text strong style={{ color: '#92400e', fontSize: 16 }}>주요 기능</Text>}
        style={{ marginBottom: 24, borderColor: '#fde68a' }}
      >
        <Row gutter={[16, 16]}>
          <Col xs={24} sm={12} md={8}>
            <Card size="small" style={{ textAlign: 'center', borderColor: '#fef3c7', height: '100%' }}>
              <DashboardOutlined style={{ fontSize: 32, color: '#f59e0b', marginBottom: 12 }} />
              <Title level={5} style={{ color: '#92400e', marginTop: 0 }}>대시보드</Title>
              <Text type="secondary" style={{ fontSize: 13 }}>
                전체/진행중/완료/보고서 준비 건수를 요약 카드로 확인. 상태 분포 파이차트와 월별 바차트 제공.
              </Text>
            </Card>
          </Col>
          <Col xs={24} sm={12} md={8}>
            <Card size="small" style={{ textAlign: 'center', borderColor: '#fef3c7', height: '100%' }}>
              <ScheduleOutlined style={{ fontSize: 32, color: '#f59e0b', marginBottom: 12 }} />
              <Title level={5} style={{ color: '#92400e', marginTop: 0 }}>시험 일정</Title>
              <Text type="secondary" style={{ fontSize: 13 }}>
                시험 목록을 테이블로 조회. 상태 필터, 키워드 검색 지원. 행 클릭으로 상세 페이지 이동.
              </Text>
            </Card>
          </Col>
          <Col xs={24} sm={12} md={8}>
            <Card size="small" style={{ textAlign: 'center', borderColor: '#fef3c7', height: '100%' }}>
              <ExperimentOutlined style={{ fontSize: 32, color: '#f59e0b', marginBottom: 12 }} />
              <Title level={5} style={{ color: '#92400e', marginTop: 0 }}>시험 상세</Title>
              <Text type="secondary" style={{ fontSize: 13 }}>
                시험번호, 장소, 포장주 등 기본정보와 처리(1~7차)/조사(1~4차) 진행 타임라인 제공.
              </Text>
            </Card>
          </Col>
          <Col xs={24} sm={12} md={8}>
            <Card size="small" style={{ textAlign: 'center', borderColor: '#fef3c7', height: '100%' }}>
              <BellOutlined style={{ fontSize: 32, color: '#f59e0b', marginBottom: 12 }} />
              <Title level={5} style={{ color: '#92400e', marginTop: 0 }}>알림</Title>
              <Text type="secondary" style={{ fontSize: 13 }}>
                시험 상태 변경, 문서 업로드 등 주요 이벤트를 알림으로 받아보세요. 읽음/안읽음 구분.
              </Text>
            </Card>
          </Col>
          <Col xs={24} sm={12} md={8}>
            <Card size="small" style={{ textAlign: 'center', borderColor: '#fef3c7', height: '100%' }}>
              <SearchOutlined style={{ fontSize: 32, color: '#f59e0b', marginBottom: 12 }} />
              <Title level={5} style={{ color: '#92400e', marginTop: 0 }}>검색 & 필터</Title>
              <Text type="secondary" style={{ fontSize: 13 }}>
                항목명, 시험번호로 검색. 진행중/완료 상태별 필터링으로 원하는 시험을 빠르게 찾기.
              </Text>
            </Card>
          </Col>
          <Col xs={24} sm={12} md={8}>
            <Card size="small" style={{ textAlign: 'center', borderColor: '#fef3c7', height: '100%' }}>
              <SafetyOutlined style={{ fontSize: 32, color: '#f59e0b', marginBottom: 12 }} />
              <Title level={5} style={{ color: '#92400e', marginTop: 0 }}>보안</Title>
              <Text type="secondary" style={{ fontSize: 13 }}>
                SSL 암호화, JWT 인증, 의뢰사별 데이터 격리. 자사 시험만 조회 가능한 접근 통제.
              </Text>
            </Card>
          </Col>
        </Row>
      </Card>

      {/* FAQ */}
      <Card
        title={<Text strong style={{ color: '#92400e', fontSize: 16 }}>자주 묻는 질문 (FAQ)</Text>}
        style={{ marginBottom: 24, borderColor: '#fde68a' }}
      >
        <Collapse
          items={faqItems}
          bordered={false}
          expandIconPosition="end"
          style={{ background: 'transparent' }}
        />
      </Card>

      {/* 시스템 정보 & 문의 */}
      <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
        <Col xs={24} md={12}>
          <Card
            title={<Text strong style={{ color: '#92400e' }}>시스템 정보</Text>}
            size="small"
            style={{ borderColor: '#fde68a', height: '100%' }}
          >
            <Space direction="vertical" size={8} style={{ width: '100%' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <Text type="secondary">포털 URL</Text>
                <Tag color="orange">partner.sappt.co.kr</Tag>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <Text type="secondary">프로토콜</Text>
                <Tag color="green">HTTPS (SSL)</Tag>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <Text type="secondary">인증 방식</Text>
                <Tag>JWT Token</Tag>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <Text type="secondary">데이터 범위</Text>
                <Tag color="gold">자사 의뢰건만</Tag>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <Text type="secondary">권한</Text>
                <Tag color="orange">조회 전용 (Read-Only)</Tag>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <Text type="secondary">버전</Text>
                <Tag>v1.0.0</Tag>
              </div>
            </Space>
          </Card>
        </Col>
        <Col xs={24} md={12}>
          <Card
            title={<Text strong style={{ color: '#92400e' }}>문의처</Text>}
            size="small"
            style={{ borderColor: '#fde68a', height: '100%' }}
          >
            <Space direction="vertical" size={16} style={{ width: '100%' }}>
              <div>
                <Text type="secondary" style={{ fontSize: 12 }}>계정 발급 / 비밀번호 초기화</Text>
                <br />
                <Text strong>시험기관 관리자에게 문의</Text>
              </div>
              <div>
                <Text type="secondary" style={{ fontSize: 12 }}>시험 진행 관련 문의</Text>
                <br />
                <Text strong>담당 연구원에게 문의</Text>
              </div>
              <div>
                <Text type="secondary" style={{ fontSize: 12 }}>시스템 오류 / 접속 문제</Text>
                <br />
                <Text strong>시스템 관리자에게 문의</Text>
              </div>
              <Divider style={{ margin: '8px 0' }} />
              <div style={{ textAlign: 'center' }}>
                <Text type="secondary" style={{ fontSize: 12 }}>
                  <ClockCircleOutlined /> 운영시간: 평일 09:00 ~ 18:00
                </Text>
              </div>
            </Space>
          </Card>
        </Col>
      </Row>
    </div>
    </div>
  );
};

export default UserGuidePage;
